import {useCallback} from 'react';
import {EventAction, EventCategory, EventNames} from 'modules/google-analytics/constants';
import {sendGtagEvent} from 'hooks/analytics/gtag/helpers';
import {useGtagEventLocation} from 'hooks/analytics/gtag/helpers/useGtagEventLocation';
import {ICartProduct} from 'modules/cart/models';

const sentMiniCartWidgetInViewKeys = new Set<string>();

interface IResult {
    handleWidgetInView: (inView: boolean) => void;
    handleAddToCartWidgetInView: () => void;
    handleMiniCartWidgetInView: (products: ICartProduct[]) => void;
    handleWidgetClick: () => void;
    handleModalShow: () => void;
    handleConditionsWidgetClick: () => void;
    handleAllProductsPopupClick: () => void;
    handleAddToCartAllProductsClick: () => void;
    handleMiniCartAllProductsClick: () => void;
    handleShieldClick: () => void;
}

// eslint-disable-next-line max-lines-per-function
export const useGrowingDiscountGtag = (): IResult => {
    const {eventLocation} = useGtagEventLocation();

    const handleWidgetInView = useCallback(
        (inView: boolean) => {
            if (!inView) {
                return;
            }

            sendGtagEvent({
                event: EventNames.OWOX,
                eventCategory: EventCategory.NonInteractions,
                eventAction: EventAction.Show,
                eventLabel: 'informer',
                eventContent: 'Растущая скидка PDP',
                eventLocation,
                portalName: 'Product',
            });
        },
        [eventLocation],
    );

    const handleAddToCartWidgetInView = useCallback(() => {
        sendGtagEvent({
            event: EventNames.OWOX,
            eventCategory: EventCategory.NonInteractions,
            eventAction: EventAction.Show,
            eventLabel: 'informer',
            eventContent: 'Растущая Скидка CartPopUp',
            eventLocation,
        });
    }, [eventLocation]);

    const handleMiniCartWidgetInView = useCallback(
        (products: ICartProduct[]) => {
            const serializedCartKey = products
                .map((product) => `${product.offerId}:${product.cartData.quantity}`)
                .sort()
                .join('|');

            if (!serializedCartKey || sentMiniCartWidgetInViewKeys.has(serializedCartKey)) {
                return;
            }

            sentMiniCartWidgetInViewKeys.add(serializedCartKey);

            sendGtagEvent({
                event: EventNames.OWOX,
                eventCategory: EventCategory.NonInteractions,
                eventAction: EventAction.Show,
                eventLabel: 'informer',
                eventContent: 'Растущая Скидка CartHeader',
                eventLocation,
            });
        },
        [eventLocation],
    );

    const handleWidgetClick = useCallback(() => {
        sendGtagEvent({
            event: EventNames.OWOX,
            eventCategory: EventCategory.Interactions,
            eventAction: EventAction.Click,
            eventLabel: 'informer',
            eventContent: 'Растущая скидка PDP',
            eventContext: 'Все товары акции',
            eventLocation,
            portalName: 'Product',
        });
    }, [eventLocation]);

    const handleModalShow = useCallback(() => {
        sendGtagEvent({
            event: EventNames.OWOX,
            eventCategory: EventCategory.NonInteractions,
            eventAction: EventAction.Show,
            eventLabel: 'informer',
            eventContent: 'Растущая скидка PopUp',
            eventLocation,
            portalName: 'Product',
        });
    }, [eventLocation]);

    const handleConditionsWidgetClick = useCallback(() => {
        sendGtagEvent({
            event: EventNames.OWOX,
            eventCategory: EventCategory.Interactions,
            eventAction: EventAction.Click,
            eventLabel: 'informer',
            eventContent: 'Растущая скидка PDP',
            eventContext: 'Условия акции',
            eventLocation,
            portalName: 'Product',
        });
    }, [eventLocation]);

    const handleAllProductsPopupClick = useCallback(() => {
        sendGtagEvent({
            event: EventNames.OWOX,
            eventCategory: EventCategory.Interactions,
            eventAction: EventAction.Click,
            eventLabel: 'informer',
            eventContent: 'Растущая скидка PopUp',
            eventContext: 'Все товары акции',
            eventLocation,
        });
    }, [eventLocation]);

    const handleAddToCartAllProductsClick = useCallback(() => {
        sendGtagEvent({
            event: EventNames.OWOX,
            eventCategory: EventCategory.Interactions,
            eventAction: EventAction.Click,
            eventLabel: 'informer',
            eventContent: 'Растущая Скидка CartPopUp',
            eventContext: 'Все товары акции',
            eventLocation,
        });
    }, [eventLocation]);

    const handleMiniCartAllProductsClick = useCallback(() => {
        sendGtagEvent({
            event: EventNames.OWOX,
            eventCategory: EventCategory.Interactions,
            eventAction: EventAction.Click,
            eventLabel: 'informer',
            eventContent: 'Растущая Скидка CartHeader',
            eventContext: 'Все товары акции',
            eventLocation,
        });
    }, [eventLocation]);

    const handleShieldClick = useCallback(() => {
        sendGtagEvent({
            event: EventNames.OWOX,
            eventCategory: EventCategory.Interactions,
            eventAction: EventAction.Click,
            eventLabel: 'informer',
            eventContent: 'Растущая Скидка Shield',
            eventContext: 'Все товары акции',
            eventLocation,
        });
    }, [eventLocation]);

    return {
        handleWidgetInView,
        handleAddToCartWidgetInView,
        handleMiniCartWidgetInView,
        handleWidgetClick,
        handleModalShow,
        handleConditionsWidgetClick,
        handleAllProductsPopupClick,
        handleAddToCartAllProductsClick,
        handleMiniCartAllProductsClick,
        handleShieldClick,
    };
};
